/**
 * Software stability (research/09 §2.4): a reading is stable when the balance says so (or gives no flag)
 * and every reading over the last 0.8 s lies within one readability step d.
 */
import { Decimal, type DecimalString } from "@/lib/decimal";
import type { BalanceReading } from "./types";

export const STABILITY_WINDOW_MS = 800;

interface Sample {
  at: number;
  grams: Decimal;
}

export class StabilityDetector {
  private samples: Sample[] = [];
  private resolution: Decimal;
  private readonly windowMs: number;

  constructor(resolution: DecimalString = "0.001", windowMs = STABILITY_WINDOW_MS) {
    this.resolution = Decimal.from(resolution);
    this.windowMs = windowMs;
  }

  setResolution(resolution: DecimalString): void {
    this.resolution = Decimal.from(resolution);
  }

  reset(): void {
    this.samples = [];
  }

  /** Adds a reading and says whether the balance is stable now. */
  push(reading: BalanceReading): boolean {
    if (reading.grams === null || reading.overload) {
      this.samples = [];
      return false;
    }
    const grams = Decimal.from(reading.grams);
    this.samples.push({ at: reading.at, grams });
    const windowStart = reading.at - this.windowMs;
    // Keep the newest sample at or before the window start: it proves the window is fully covered.
    while (this.samples.length > 1 && (this.samples[1]?.at ?? Infinity) <= windowStart) this.samples.shift();
    if (reading.stable === false) return false;
    const oldest = this.samples[0];
    if (!oldest || oldest.at > windowStart) return false;
    let low = grams;
    let high = grams;
    for (const sample of this.samples) {
      if (sample.grams.compare(low) < 0) low = sample.grams;
      if (sample.grams.compare(high) > 0) high = sample.grams;
    }
    return high.minus(low).compare(this.resolution) <= 0;
  }
}
